import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import BasicModal from '../../../components/BasicModal';
import FormInput from '../../../components/FormInput';
import MyAppText from '../../../components/MyAppText';
import { appAgentService } from '../../../services/central-api/app-agent';
import { ClientError, InvalidValue, UserNotInUnit } from '../../../services/central-api/base-service';
import { i18nKeys, translate } from '../../../services/translate';
import { FUEL_LEVELS } from './CreateOseModal';
import OseFieldLabel from './OseFieldLabel';
import FontWeight from '../../../styles/fontWeights';

const STAGES: string[] = ['before_departure', 'during_service', 'after_service'];

const STAGE_TITLES: Record<string, i18nKeys> = {
    before_departure: 'oseMomentBeforeDeparture',
    during_service: 'oseMomentDuringService',
    after_service: 'oseMomentAfterService',
};

const ADVANCE_LABELS: Record<string, i18nKeys> = {
    before_departure: 'oseConfirmDeparture',
    during_service: 'oseEndService',
    after_service: 'oseFinish',
};

interface ChecklistFormItem {
    id: number;
    checklistValue: keyof VehicleChecklistValues;
    status: boolean | null;
    observation: string;
}

interface OseChecklistForm {
    items: ChecklistFormItem[];
    endFuelLevel: number | null;
}

const EMPTY_FORM: OseChecklistForm = {
    items: [],
    endFuelLevel: null,
};

function toForm(ose: AppAgentOseDetails): OseChecklistForm {
    return {
        items: ose.checklistItems.map((item) => ({
            id: item.id,
            checklistValue: item.checklistValue,
            status: item.status,
            observation: item.observation ?? '',
        })),
        endFuelLevel: ose.endFuelLevel,
    };
}

function getLoadErrorMessage(err: unknown): string {
    if (err instanceof ClientError && err.message === 'OseNotEditable') {
        return translate('oseNotEditable');
    }

    if (err instanceof UserNotInUnit) {
        return translate('oseUserNotInUnitEdit');
    }

    return translate('oseLoadError');
}

function getSaveErrorMessage(err: unknown): string {
    if (err instanceof UserNotInUnit) {
        return translate('oseUserNotInUnitEdit');
    }

    if (err instanceof InvalidValue && err.field === 'endFuelLevel') {
        return translate('oseEndFuelRequired');
    }

    if (err instanceof ClientError) {
        if (err.message === 'OseNotEditable') {
            return translate('oseNotEditable');
        }
        if (err.message === 'OseItemsPending') {
            return translate('oseItemsPending');
        }
        if (err.message === 'OseStageMismatch') {
            return translate('oseStageMismatch');
        }
    }

    return translate('oseSaveError');
}

interface Props {
    team: AppAgentUnit | null;
    oseId: number | null;
    visible: boolean;
    setVisible: (value: boolean) => void;
    onChanged: () => Promise<unknown>;
}

export default function OseChecklistModal({ team, oseId, visible, setVisible, onChanged }: Props) {
    const { styles } = useStyles(styleSheet);
    const queryClient = useQueryClient();
    const keepOpenRef = useRef(false);
    const submittingRef = useRef(false);
    const scrollRef = useRef<ScrollView>(null);

    const [finishing, setFinishing] = useState(false);
    const [current, setCurrent] = useState<AppAgentOseDetails | null>(null);

    const { data: queryData, isLoading, isError, error } = useQuery({
        queryKey: ['getOse', oseId],
        queryFn: () => appAgentService.getOse(oseId!),
        enabled: visible && oseId !== null && !finishing,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const data = current;

    const form = useForm<OseChecklistForm>({ defaultValues: EMPTY_FORM });
    const { isDirty } = form.formState;
    const items = useWatch({ control: form.control, name: 'items' });
    const endFuelLevel = useWatch({ control: form.control, name: 'endFuelLevel' });

    const save = useMutation({
        mutationFn: (vars: { oseId: number; body: SaveOseChecklistBody; }) => {
            return appAgentService.saveOseChecklist(vars.oseId, vars.body);
        },
    });

    useEffect(() => {
        if (visible && data) {
            form.reset(toForm(data));
        }
    }, [visible, data]);

    useEffect(() => {
        keepOpenRef.current = false;
        setFinishing(false);
        setCurrent(null);
    }, [visible, oseId]);

    useEffect(() => {
        if (visible && queryData) {
            setCurrent(queryData);
            form.reset(toForm(queryData));
            scrollRef.current?.scrollTo({ y: 0, animated: false });
        }
    }, [visible, queryData]);

    function handleSetVisible(value: boolean) {
        if (!value && keepOpenRef.current) {
            keepOpenRef.current = false;
            return;
        }
        setVisible(value);
    }

    const currentMoment = data?.currentMoment ?? '';
    const isFinalStage = currentMoment === 'after_service';
    const canSaveWithoutAdvance = currentMoment === 'during_service';
    const needsEndFuel = !!team?.unitName.unitCategory?.endFuelLevel;
    const expectedCount = data?.checklistItems.length ?? 0;
    const formSynced = data !== undefined && items.length === expectedCount;
    const allAnswered = formSynced && items.every((item) => item.status !== null);
    const canAdvance = allAnswered && (!isFinalStage || !needsEndFuel || endFuelLevel !== null);

    async function reloadOse(id: number) {
        const fresh = await appAgentService.getOse(id);

        setCurrent(fresh);
        queryClient.setQueryData(['getOse', id], fresh);
        form.reset(toForm(fresh));
        scrollRef.current?.scrollTo({ y: 0, animated: false });
    }

    async function submit(advance: boolean) {
        if (!data || submittingRef.current) {
            return;
        }

        if (!advance && !isDirty) {
            return;
        }

        submittingRef.current = true;

        try {
            const values = form.getValues();
            const willFinish = advance && isFinalStage;

            if (willFinish) {
                setFinishing(true);
            }

            try {
                await save.mutateAsync({
                    oseId: data.id,
                    body: {
                        fromMoment: data.currentMoment,
                        advance,
                        endFuelLevel: values.endFuelLevel,
                        items: values.items.map((item) => ({
                            id: item.id,
                            status: item.status,
                            observation: item.status === false ? (item.observation.trim() || null) : null,
                        })),
                    },
                });
            } catch (err) {
                console.error(err);

                Toast.show({
                    type: 'sentinelxError',
                    text1: getSaveErrorMessage(err),
                });

                const isClosed = err instanceof ClientError && err.message === 'OseNotEditable';

                if (!isClosed) {
                    setFinishing(false);
                }

                try {
                    if (!isClosed) {
                        await reloadOse(data.id);
                    }
                    await onChanged();
                } catch (syncErr) {
                    console.error(syncErr);
                }

                keepOpenRef.current = advance && !isClosed;
                return;
            }

            const movesToFinalStage = advance && data.currentMoment === 'during_service';
            let stayOpen = false;

            try {
                if (!advance || movesToFinalStage) {
                    await reloadOse(data.id);
                    stayOpen = movesToFinalStage;
                }
                await onChanged();
            } catch (syncErr) {
                console.error(syncErr);
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            keepOpenRef.current = stayOpen;
        } finally {
            submittingRef.current = false;
        }
    }

    function handleAdvance() {
        return submit(true);
    }

    function handleSave() {
        return submit(false);
    }

    function setAnswer(index: number, status: boolean) {
        form.setValue(`items.${index}.status`, status, { shouldDirty: true });
    }

    function renderBody(ose: AppAgentOseDetails) {
        const stageNumber = STAGES.indexOf(ose.currentMoment) + 1;

        return (
            <>
                <View style={styles.header}>
                    <MyAppText style={styles.stageProgress}>
                        {translate('oseStepProgress', { current: stageNumber, total: STAGES.length })}
                    </MyAppText>
                    <MyAppText style={styles.stageTitle}>
                        {translate(STAGE_TITLES[ose.currentMoment])}
                    </MyAppText>
                </View>

                <ScrollView
                    ref={scrollRef}
                    style={styles.scroll}
                    contentContainerStyle={styles.list}
                    showsVerticalScrollIndicator={false}
                >
                    {isFinalStage && needsEndFuel && (
                        <View style={styles.itemCard}>
                            <OseFieldLabel label={translate('oseEndFuelLevel')} labelStyle={styles.itemLabel} required />
                            <View style={styles.chipsRow}>
                                {FUEL_LEVELS.map((level) => {
                                    const selected = endFuelLevel === level;

                                    return (
                                        <TouchableOpacity
                                            key={level}
                                            style={[styles.chip, selected && styles.chipSelected]}
                                            onPress={() => form.setValue('endFuelLevel', level, { shouldDirty: true })}
                                        >
                                            <MyAppText style={[styles.chipText, selected && styles.chipTextSelected]}>
                                                {`${level}%`}
                                            </MyAppText>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                        </View>
                    )}

                    {items.length === 0 && (
                        <View style={styles.itemCard}>
                            <MyAppText style={styles.muted}>{translate('oseStageWithoutItems')}</MyAppText>
                        </View>
                    )}

                    {items.map((item, index) => (
                        <View key={item.id} style={styles.itemCard}>
                            <MyAppText style={styles.itemLabel}>{translate(item.checklistValue)}</MyAppText>

                            <View style={styles.answerRow}>
                                <TouchableOpacity
                                    style={[styles.answerButton, item.status === true && styles.compliantSelected]}
                                    onPress={() => setAnswer(index, true)}
                                >
                                    <MyAppText style={[styles.answerText, item.status === true && styles.answerTextSelected]}>
                                        {translate('compliant')}
                                    </MyAppText>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    style={[styles.answerButton, item.status === false && styles.nonCompliantSelected]}
                                    onPress={() => setAnswer(index, false)}
                                >
                                    <MyAppText style={[styles.answerText, item.status === false && styles.answerTextSelected]}>
                                        {translate('nonCompliant')}
                                    </MyAppText>
                                </TouchableOpacity>
                            </View>

                            {item.status === false && (
                                <FormInput
                                    form={form}
                                    fieldName={`items.${index}.observation`}
                                    placeholder={translate('oseObservationPlaceholder')}
                                    multiline
                                    numberOfLines={3}
                                    inputStyle={styles.observation}
                                />
                            )}
                        </View>
                    ))}
                </ScrollView>
            </>
        );
    }

    return (
        <BasicModal
            isModalVisible={visible}
            setModalVisible={handleSetVisible}
            width='small'
            mobileHeight='fill'
            title={translate('ose')}
            disableScrollView
            primaryButton={data ? {
                text: translate(ADVANCE_LABELS[data.currentMoment]),
                disabled: !canAdvance || save.isPending,
                onClick: handleAdvance,
            } : undefined}
            secondaryButton={data && canSaveWithoutAdvance && expectedCount > 0 ? {
                text: translate('oseSaveChanges'),
                onClick: handleSave,
            } : undefined}
        >
            {isLoading || !data
                ? (
                    <View style={styles.center}>
                        {isError
                            ? <MyAppText style={styles.muted}>{getLoadErrorMessage(error)}</MyAppText>
                            : <ActivityIndicator size='large' />
                        }
                    </View>
                )
                : renderBody(data)
            }
        </BasicModal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    muted: {
        fontSize: 14,
        color: theme.colors.neutral['500'],
        textAlign: 'center',
    },
    scroll: {
        flex: 1,
    },
    header: {
        gap: 2,
    },
    stageProgress: {
        fontSize: 11,
        fontWeight: FontWeight.semiBold,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: theme.colors.neutral['400'],
    },
    stageTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: theme.colors.field.text,
    },
    list: {
        gap: 10,
        paddingBottom: 8,
    },
    itemCard: {
        gap: 10,
        padding: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.neutral['200'],
    },
    itemLabel: {
        fontSize: 14,
        fontWeight: FontWeight.semiBold,
        color: theme.colors.field.text,
    },
    answerRow: {
        flexDirection: 'row',
        gap: 8,
    },
    answerButton: {
        flex: 1,
        paddingVertical: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
    },
    compliantSelected: {
        backgroundColor: theme.colors.badges.green.textColor,
        borderColor: theme.colors.badges.green.textColor,
    },
    nonCompliantSelected: {
        backgroundColor: theme.colors.errors[300],
        borderColor: theme.colors.errors[300],
    },
    answerText: {
        fontSize: 13,
        fontWeight: FontWeight.semiBold,
        color: theme.colors.neutral['500'],
    },
    answerTextSelected: {
        color: theme.colors.white['500'],
    },
    observation: {
        minHeight: 60,
        textAlignVertical: 'top',
    },
    chipsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    chip: {
        paddingVertical: 8,
        paddingHorizontal: 14,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
    },
    chipSelected: {
        backgroundColor: theme.colors.primary['500'],
        borderColor: theme.colors.primary['500'],
    },
    chipText: {
        fontSize: 14,
        color: theme.colors.neutral['500'],
    },
    chipTextSelected: {
        color: theme.colors.white['500'],
        fontWeight: FontWeight.semiBold,
    },
}));
